import { Directory, Filesystem } from "@capacitor/filesystem";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/backend";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { Capacitor } from "@/lib/capacitor";
import { App } from "@/lib/capacitor/app";
import { Device } from "@/lib/capacitor/device";
import { LocalNotifications } from "@/lib/capacitor/local-notifications";
import { PushNotifications } from "@/lib/capacitor/push-notifications";
import { useMe } from "@/lib/hooks/use-me";
import { queryOptions_pushNotificationsPermissions } from "@/lib/providers/query/functions/@capacitor/push-notifications/permissions";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";
import { authClient } from "@/utils/auth";

// Feature flag: Enable/disable organization images in push notifications
const ENABLE_NOTIFICATION_IMAGES = false;

// Define notification data schema
const NotificationDataSchema = z
  .object({
    path: z.string().optional(), // URL to navigate to when notification is tapped
    type: z.string().optional(), // Optional type for analytics/logging
    imageUrl: z.string().optional(), // Organization logo URL from backend
    organizationId: z.string().optional(), // Organization ID for multi-tenant switching
    organizationSlug: z.string().optional(), // Organization slug for multi-tenant switching
    metadata: z.record(z.unknown()).optional(),
  })
  .passthrough();

type NotificationData = z.infer<typeof NotificationDataSchema>;

/**
 * Downloads an image from a URL and saves it to the local filesystem for use in notifications.
 * Returns a file:// URL that can be used in LocalNotifications attachments.
 */
async function downloadNotificationImage(imageUrl: string): Promise<string | null> {
  try {
    logger.info({ imageUrl, platform: Capacitor.Platform }, "Starting image download");

    const filename = `notif_${btoa(imageUrl)
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 50)}.png`;

    // Check if file already exists in cache
    try {
      const existingFile = await Filesystem.stat({
        path: `notifications/${filename}`,
        directory: Directory.Cache,
      });
      logger.info({ filename, uri: existingFile.uri }, "Found cached notification image");
      return existingFile.uri;
    } catch (cacheError) {
      logger.info({ filename }, "File not in cache, will download");
    }

    // Download the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      logger.error({ status: response.status, imageUrl }, "Failed to fetch notification image");
      return null;
    }

    const blob = await response.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64Data = result.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Ensure directory exists
    try {
      await Filesystem.mkdir({
        path: "notifications",
        directory: Directory.Cache,
        recursive: true,
      });
    } catch (mkdirError) {
      // Directory might already exist
    }

    // Save to filesystem
    const writeResult = await Filesystem.writeFile({
      path: `notifications/${filename}`,
      data: base64,
      directory: Directory.Cache,
    });

    logger.info({ filename, uri: writeResult.uri }, "Successfully saved notification image");
    return writeResult.uri;
  } catch (error) {
    logger.error({ error, imageUrl }, "Error downloading notification image");
    return null;
  }
}

/**
 * Handles navigation after a notification is tapped. Checks if organization switch is needed and
 * navigates accordingly.
 */
function handleNotificationNavigation(
  data: NotificationData,
  currentOrgId: string | null,
  source: "local" | "push",
) {
  const { path, type, organizationId: notificationOrgId } = data;

  logger.info(
    {
      notificationOrgId,
      currentOrgId,
      needsSwitch: notificationOrgId && currentOrgId && notificationOrgId !== currentOrgId,
      source,
    },
    "🔍 Organization check for navigation",
  );

  // Check if we need to switch organizations before navigating
  if (notificationOrgId && currentOrgId && notificationOrgId !== currentOrgId) {
    logger.info({ currentOrgId, notificationOrgId }, "🔄 Switching to different organization");

    // Navigate to the switch organization screen with target org and callback
    const callbackUrl = path || Routes.App();
    const switchUrl = Routes.SwitchOrganization(undefined, {
      search: {
        targetOrgId: notificationOrgId,
        callback: callbackUrl,
      },
    });

    logger.info(
      {
        switchUrl,
        notificationOrgId,
        callbackUrl,
        rawSearchParams: { targetOrgId: notificationOrgId, callback: callbackUrl },
      },
      "➡️ Navigating to switch screen with full details",
    );
    history.push(switchUrl);
    return;
  }

  logger.info("ℹ️ No organization switch needed - same org or no org in notification");

  // Normal navigation (same organization or no organization switch needed)
  if (path) {
    logger.info({ path, type }, `➡️ Navigating to path: ${path}`);
    history.push(path);
  } else {
    logger.info("🏠 No path provided, going to app home");
    history.push(Routes.App());
  }
}

export function NotificationListenerPush() {
  const hasRegisteredRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const processedNotificationsRef = useRef<Set<string>>(new Set());

  // Update location ref and log changes
  useEffect(() => {
    logger.debug("Location changed", {
      newPath: location.pathname,
      previousPath: locationRef.current,
    });
    locationRef.current = location.pathname;
  }, [location.pathname]);

  const { me, sessionProvider } = useMe();
  const userId = me?.id ?? null;
  const organizationId = sessionProvider.session.data?.session.organizationId ?? null;
  const queryClient = useQueryClient();

  const { data: permissionState } = useQuery(queryOptions_pushNotificationsPermissions());

  const mutationAddDevice = useExtractedMutation({
    mutationKey: ["notification", "add-device"],
    mutationFn: api.notification.addDevice,
  });

  // Set up notification listeners
  useEffect(() => {
    if (Capacitor.Platform === "web") {
      return;
    }

    logger.info("🎧 Setting up push notification listeners", { organizationId });

    // Registration success listener
    PushNotifications.addRegistrationListener(async (token) => {
      logger.debug({ token }, "Push Registration Success");
      try {
        const deviceIdentifier = await Device.getDeviceIdentifier();
        const deviceName = await Device.getDeviceName();
        const platform = Capacitor.Platform;

        // Get fresh organizationId from session provider to avoid stale closure
        const currentOrganizationId = sessionProvider.session.data?.session.organizationId;
        if (!currentOrganizationId) {
          logger.error("Cannot register device: organizationId is not available in session");
          return;
        }

        // Get app identifier for multi-app filtering
        const appIdentifier = import.meta.env.VITE_ORGANIZATION_SLUG || "boomer";

        await mutationAddDevice.mutateAsync({
          token,
          name: deviceName,
          deviceIdentifier,
          platform,
          organizationId: currentOrganizationId,
        });

        logger.info("Device registered successfully with backend", {
          deviceIdentifier,
          platform,
          organizationId: currentOrganizationId,
        });
      } catch (error) {
        logger.error({ error }, "Failed to register device with backend");
      }
    });

    // Registration error listener
    PushNotifications.addRegistrationErrorListener((error) =>
      logger.error({ error }, "Push Notification Registration Error"),
    );

    // Tap handler for LocalNotifications (notifications shown when app was in FOREGROUND)
    LocalNotifications.addLocalNotificationActionPerformedListener(async (action) => {
      logger.info({ action }, "🔔 LocalNotification Tapped (app was in foreground)");

      // FILTER: Check if user is still logged in before handling tap
      if (!userId) {
        logger.info("❌ User not logged in - ignoring notification tap");
        return;
      }

      try {
        const rawData = action.notification.extra || {};
        logger.info({ rawData }, "📦 Raw notification data");

        const data = NotificationDataSchema.safeParse(rawData);
        if (!data.success) {
          logger.warn({ error: data.error, rawData }, "❌ Failed to parse notification data");
          return;
        }

        logger.info({ parsedData: data.data }, "✅ Parsed notification data");

        handleNotificationNavigation(data.data, organizationId, "local");
      } catch (error) {
        logger.error({ error }, "❌ Error handling notification navigation");
      }
    });

    // Notification received listener - ONLY fires when app is in FOREGROUND
    // We schedule as LocalNotification so we can control display and handle taps via LocalNotificationActionPerformedListener
    PushNotifications.addPushNotificationReceivedListener(async (notification) => {
      logger.info({ notification }, "📨 Push Notification Received (app in foreground)");

      try {
        const rawData = notification.data || {};

        // Create unique ID for this notification to prevent duplicates
        const notificationId = notification.id || Date.now().toString();

        // Check if we've already processed this notification
        if (processedNotificationsRef.current.has(notificationId)) {
          logger.info({ notificationId }, "⏭️ Skipping duplicate notification");
          return;
        }

        // Mark as processed
        processedNotificationsRef.current.add(notificationId);

        // Clean up old entries after 30 seconds to prevent memory leak
        setTimeout(() => {
          processedNotificationsRef.current.delete(notificationId);
        }, 30000);

        logger.info({ rawData, notificationId }, "📦 Notification raw data");
        const data = NotificationDataSchema.safeParse(rawData);

        if (!data.success) {
          logger.warn({ error: data.error, rawData }, "Failed to parse notification data");

          // CRITICAL: Still check if user is logged in before showing unparseable notification
          if (!userId) {
            logger.info("❌ User not logged in - suppressing unparseable notification", {
              notificationId,
            });
            return;
          }

          // For unparseable notifications in standalone apps, suppress them (safest option)
          const appSlug = import.meta.env.VITE_ORGANIZATION_SLUG;
          if (appSlug && appSlug !== "boomer") {
            logger.warn("❌ Suppressing unparseable notification in standalone app", {
              appSlug,
              notificationId,
            });
            return;
          }

          // Only Boomer whitelabel app shows unparseable notifications
          logger.info("⚠️ Showing unparseable notification in Boomer whitelabel app");
          await scheduleLocalNotification(notification, rawData);
          return;
        }

        const {
          path,
          organizationId: notificationOrgId,
          organizationSlug: notificationOrgSlug,
        } = data.data;

        // FILTER 1: Check if user is logged in
        if (!userId) {
          logger.info("❌ User not logged in - suppressing notification", { notificationId });
          return;
        }

        // FILTER 2: App-specific filtering (standalone apps only show their own notifications)
        const appSlug = import.meta.env.VITE_ORGANIZATION_SLUG;
        if (appSlug && appSlug !== "boomer") {
          // This is a standalone app (piekfijn, jongens-van-de-bar, etc.)
          // Require organizationSlug to be present for security
          if (!notificationOrgSlug) {
            logger.warn(
              "❌ Suppressing notification - missing organizationSlug in standalone app",
              {
                currentAppSlug: appSlug,
                notificationId,
              },
            );
            return;
          }

          // Only show notifications for THIS specific app
          if (notificationOrgSlug !== appSlug) {
            logger.info("❌ Suppressing notification - wrong app (standalone app filter)", {
              notificationOrgSlug,
              currentAppSlug: appSlug,
              notificationId,
            });
            return;
          }
        }
        // Boomer whitelabel app (appSlug === 'boomer' or undefined): show all notifications

        // FILTER 3: Organization mismatch - check if user needs to switch org
        const needsOrgSwitch =
          notificationOrgId && organizationId && notificationOrgId !== organizationId;

        logger.info("🔍 Organization check in received notification", {
          notificationOrgId,
          currentOrgId: organizationId,
          notificationOrgSlug,
          currentAppSlug: appSlug,
          needsOrgSwitch,
        });

        // If notification is from different org, always show it (don't suppress)
        if (!needsOrgSwitch) {
          // FILTER 4: Same-page suppression (only suppress if same organization and same page)
          if (path) {
            const currentPath = locationRef.current || window.location.pathname || "";
            const isViewingSamePage = currentPath === path;

            logger.info("🔍 Checking if notification should be filtered (same page)", {
              notificationPath: path,
              currentPath,
              isViewingSamePage,
            });

            if (isViewingSamePage) {
              logger.info("❌ Suppressing notification - user is viewing this page", { path });
              return;
            }

            logger.info("✅ Not filtering - user is on different page", { path, currentPath });
          }
        } else {
          logger.info("✅ Notification from different org - will show even if on same page", {
            notificationOrgId,
            currentOrgId: organizationId,
          });
        }

        // Show the notification by scheduling it locally
        await scheduleLocalNotification(notification, rawData);
      } catch (error) {
        logger.error({ error }, "Error handling push notification");
        // On error, try to show the notification anyway
        try {
          await LocalNotifications.schedule({
            id: notification.id ? parseInt(String(notification.id)) : Date.now(),
            title: notification.title || "New notification",
            body: notification.body || "",
            extra: notification.data || {},
          });
        } catch (scheduleError) {
          logger.error({ error: scheduleError }, "Failed to schedule notification as fallback");
        }
      }
    });

    return () => {
      PushNotifications.removeAllListeners();
      LocalNotifications.removeAllListeners();
    };
    // Only re-setup listeners when organizationId or userId changes (stable values)
    // mutationAddDevice and sessionProvider are used via closure but don't trigger re-setup
  }, [organizationId, userId]);

  // Register for push notifications when permission is granted and user is logged in
  useEffect(() => {
    if (Capacitor.Platform === "web") {
      return;
    }

    // Only register if we have a userId and permission is granted
    // permissionState can be either a string ("granted", "denied", etc.) or an object with { receive: string }
    const receivePermission =
      permissionState && typeof permissionState === "object" && "receive" in permissionState
        ? (permissionState as { receive: string }).receive
        : permissionState;
    if (!userId || receivePermission !== "granted") {
      logger.info("Skipping push notification registration", {
        hasUserId: !!userId,
        permissionState: receivePermission,
      });
      return;
    }

    // Only register once per user
    if (hasRegisteredRef.current && currentUserIdRef.current === userId) {
      logger.info("Already registered for push notifications", { userId });
      return;
    }

    const registerForPushNotifications = async () => {
      try {
        logger.info("Registering for push notifications", { userId });
        await PushNotifications.register();
        hasRegisteredRef.current = true;
        currentUserIdRef.current = userId;
      } catch (error) {
        logger.error({ error }, "Failed to register for push notifications");
      }
    };

    registerForPushNotifications();
  }, [permissionState, userId]);

  return null;
}

/** Helper function to schedule a local notification with optional image attachment */
async function scheduleLocalNotification(
  notification: any,
  rawData: Record<string, unknown>,
): Promise<void> {
  const notificationPayload: {
    id: number;
    title: string;
    body: string;
    extra?: Record<string, unknown>;
    attachments?: Array<{ id: string; url: string }>;
  } = {
    id: notification.id ? parseInt(String(notification.id)) : Date.now(),
    title: notification.title || "New notification",
    body: notification.body || "",
    extra: rawData,
  };

  // Download and attach organization logo if feature is enabled and image URL is provided
  if (ENABLE_NOTIFICATION_IMAGES && rawData.imageUrl && typeof rawData.imageUrl === "string") {
    logger.info({ imageUrl: rawData.imageUrl }, "Downloading notification image");
    const localImageUrl = await downloadNotificationImage(rawData.imageUrl);
    if (localImageUrl) {
      notificationPayload.attachments = [{ id: "org-logo", url: localImageUrl }];
      logger.info({ localImageUrl }, "Attached organization logo to notification");
    } else {
      logger.warn({ imageUrl: rawData.imageUrl }, "Failed to download notification image");
    }
  }

  try {
    await LocalNotifications.schedule(notificationPayload);
    logger.info("Notification scheduled successfully");
  } catch (scheduleError) {
    logger.error({ error: scheduleError }, "Failed to schedule notification");
    throw scheduleError;
  }
}
